<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>
    <div class="row mt-2 overflow-scroll">
      <div class="col-12">
        <card class="no-scrollbar" style="overflow-x: scroll">
          <tab-rail
            class="mb-3"
            :tabs="categories"
            :value="activeTabIndex"
            @input="switchTab"
          >
            <template #actions>
              <base-button v-if="activeTabIndex === 0 && hasPermissions('gift_cards_update')" type="primary" size="sm" v-on:click="newGiftCard()">
                <i class="tim-icons el-icon-plus mr-1"></i> <span class="d-none d-sm-inline">Create a gift card purchase</span>
              </base-button>
              <base-button v-if="activeTabIndex === 1 && hasPermissions('gift_cards_update')" type="primary" size="sm" v-on:click="newGiftCardType()">
                <i class="tim-icons el-icon-plus mr-1"></i><span class="d-none d-sm-inline">Create a gift card type</span>
              </base-button>
            </template>
          </tab-rail>

<!--          Purchases-->
          <div v-if="activeTabIndex === 0">
            <filter-bar
              :filters="filterSchema"
              :initial-value="filterValues"
              :result-count="totalRows"
              :per-page="perPage"
              :per-page-options="perPageOptions"
              saved-filters-key="gift-cards"
              @change="onFiltersChanged"
              @update:perPage="onPerPageChange"
            >
              <template #actions>
                <base-button v-if="selectedGiftCards.length > 0 || select_all_gift_cards" @click="bulkUpdatePrompt()" link style="padding: 0" type="primary" size="sm">
                  Bulk actions
                </base-button>
                <base-button v-if="hasPermissions('download_data')" icon class="btn-link text-white" v-on:click="downloadCSV()">
                  <i class="tim-icons icon-cloud-download-93"></i>
                </base-button>
              </template>
            </filter-bar>

<!--            List-->
            <div class="mt-3" v-if="gift_purchases.length <= 0">
              <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
                <h4 class="mt-3">No gift purchases have been made.</h4>
              </div>
            </div>

            <!--        Mobile-->
            <div class="row d-sm-none d-block">
              <div class="col">
                <b-table v-if="gift_purchases.length > 0" :items="gift_purchases"
                         :fields="['gift_cards']">
                  <template v-slot:head(gift_cards)="row">
                    <span>Total results: {{totalRows}}</span>
                  </template>
                  <template v-slot:cell(gift_cards)="row">
                    <div class="row mb-2">
                      <div class="col">
                        <div style="display: flex; align-items: center;">
                          <code class="gc-code gc-code--block" style="flex: 1;">{{getGiftCardCode(row.item.code)}}</code>
                          <el-tooltip content="Copy code" effect="light" :open-delay="300" placement="top">
                            <base-button class="btn btn-link p-0 ml-2 gc-copy-btn" icon simple size="sm" type="primary" @click.stop="copyGiftCardCode(row.item.code)">
                              <i class="tim-icons el-icon-copy-document"></i>
                            </base-button>
                          </el-tooltip>
                        </div>
                      </div>
                    </div>
                    <div class="row mb-2">
                      <div class="col">
                        <div class="gc-meta">
                          <span v-if="getGiftCardTypeName(row.item.gift_card_type_id)" class="badge badge-default mb-0">
                            <i class="tim-icons icon-tag"></i>{{ getGiftCardTypeName(row.item.gift_card_type_id) }}
                          </span>
                          <div v-if="row.item.is_refund_gift_card" class="gc-info-badge gc-info-badge--refund">
                            <i class="tim-icons icon-refresh-02"></i>
                            <span>Refund</span>
                          </div>
                          <el-tooltip v-if="row.item.suppress_confirmation_email" content="The purchase confirmation email to the customer was turned off at purchase." effect="light" :open-delay="300" placement="top">
                            <span class="badge badge-default mb-0">
                              <i class="tim-icons icon-email-85"></i>No confirmation email
                            </span>
                          </el-tooltip>
                          <el-tooltip v-if="row.item.suppress_delivery_email" content="The delivery email to the recipient was turned off at purchase. Use Send gift card email to deliver it manually." effect="light" :open-delay="300" placement="top">
                            <span class="badge badge-default mb-0">
                              <i class="tim-icons icon-send"></i>No delivery email
                            </span>
                          </el-tooltip>
                          <utm-attribution
                            v-if="enable_utm_source_tracking && row.item.utm_source"
                            icon="icon-world"
                            :source="row.item.utm_source"
                            :medium="row.item.utm_medium"
                            :campaign="row.item.utm_campaign"
                            :referrer="row.item.utm_referrer" />
                          <span v-if="row.item.is_active" class="gc-status gc-status--active">Active</span>
                          <span v-if="!row.item.is_active" class="gc-status gc-status--inactive">Inactive</span>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <div class="people-cell">
                          <div class="person-entry">
                            <div class="person-header">
                              <span class="person-label person-label--from">From</span>
                              <span class="person-name text-white">{{ row.item.first_name }} {{ row.item.last_name }}</span>
                              <el-tooltip content="View customer" effect="light" :open-delay="300" placement="top">
                                <base-button v-if="hasPermissions('customer_view')" @click="viewCustomer(row.item.customer_id)" icon class="btn btn-link p-0 ml-1" simple size="sm" type="primary">
                                  <i class="tim-icons el-icon-top-right"></i>
                                </base-button>
                              </el-tooltip>
                            </div>
                            <div class="person-email"><i class="tim-icons icon-email-85"></i> {{ row.item.email }}</div>
                          </div>
                          <div v-for="(r, i) in getRecipients(row.item.recipients)" :key="'R' + i" class="person-entry person-entry--recipient">
                            <div class="person-header">
                              <span class="person-label person-label--to">To</span>
                              <span class="person-name text-white">{{ r.first_name }} {{ r.last_name }}</span>
                              <el-tooltip content="View customer" effect="light" :open-delay="300" placement="top">
                                <base-button v-if="hasPermissions('customer_view')" @click="viewCustomer(r.user_id)" icon class="btn btn-link p-0 ml-1" simple size="sm" type="primary">
                                  <i class="tim-icons el-icon-top-right"></i>
                                </base-button>
                              </el-tooltip>
                            </div>
                            <div class="person-email"><i class="tim-icons icon-email-85"></i> {{ r.email }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row mt-1">
                      <div class="col">
                        <small style="text-transform: uppercase; letter-spacing: 0.5px; font-size: 10px; font-weight: 600;">Dates</small>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <small class="text-white">
                          Purchase date: {{getLocalDate(row.item.date_purchased)}}, {{getLocalTime(row.item.date_purchased)}}
                        </small>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <small class="text-white">
                          Delivery date: {{ getDeliveryDate(row.item.delivery_date, row.item.date_purchased) }}
                        </small>
                      </div>
                    </div>

                    <div class="row mt-1">
                      <div class="col">
                        <small style="text-transform: uppercase; letter-spacing: 0.5px; font-size: 10px; font-weight: 600;">Value</small>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <small class="text-white">Value: {{ getAmount(row.item.total) }}</small>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <small class="text-white">Price: {{ getAmount(row.item.price) }}</small>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <small class="text-white">Remaining: {{ getAmount(row.item.remaining) }}</small>
                      </div>
                    </div>
                    <div class="row" v-if="hasFinancialDetails(row.item)">
                      <div class="col">
                        <el-popover trigger="click" placement="right" width="220" popper-class="gc-details-popover">
                          <div>
                            <div class="gc-pop-section">Gift card</div>
                            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                              <span class="gc-pop-label">Value</span>
                              <span class="gc-pop-value">{{ getAmount(row.item.total) }}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                              <span class="gc-pop-label">Price</span>
                              <span class="gc-pop-value">{{ getAmount(row.item.price) }}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                              <span class="gc-pop-label">Remaining</span>
                              <span class="gc-pop-value">{{ getAmount(row.item.remaining) }}</span>
                            </div>
                            <div v-if="row.item.discount > 0" style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                              <span class="gc-pop-label">Discount</span>
                              <span class="gc-pop-value">-{{ getAmount(row.item.discount) }}</span>
                            </div>
                            <div v-if="row.item.fee > 0" style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                              <span class="gc-pop-label">Fee</span>
                              <span class="gc-pop-value">{{ getAmount(row.item.fee) }}</span>
                            </div>
                          </div>
                          <div class="gc-pop-divider"></div>
                          <div>
                            <div class="gc-pop-section">Transaction</div>
                            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                              <span class="gc-pop-label">Paid</span>
                              <span class="gc-pop-value">{{ getAmount(row.item.transaction_paid) }}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                              <span class="gc-pop-label">Total</span>
                              <span class="gc-pop-value">{{ getAmount(row.item.transaction_total) }}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between;">
                              <span class="gc-pop-label">Due</span>
                              <span class="gc-pop-value">{{ getAmount(row.item.transaction_due) }}</span>
                            </div>
                          </div>
                          <span slot="reference" style="font-size: 11px; color: #e14eca; cursor: pointer;">
                            More details <i class="tim-icons icon-minimal-down" style="font-size: 9px; margin-left: 2px;"></i>
                          </span>
                        </el-popover>
                      </div>
                    </div>

                    <div class="row mt-2">
                      <div class="col">
                        <el-dropdown trigger="click">
                          <base-button simple type="primary" size="sm">
                            Actions <i class="tim-icons icon-minimal-down ml-1"></i>
                          </base-button>
                          <template #dropdown>
                            <el-dropdown-menu>
                              <a v-if="hasPermissions('gift_cards_update')"
                                 class="dropdown-item mt-2 mb-2" @click="editGiftCardPrompt(row.item, $event)" href="#">
                                <i class="tim-icons icon-pencil text-default mr-2"></i> Edit gift card
                              </a>
                              <a v-if="hasPermissions('gift_cards_update') && row.item.is_active"
                                 class="dropdown-item mt-2 mb-2" @click="openAdjustValueModal(row.item, $event)" href="#">
                                <i class="tim-icons icon-money-coins text-default mr-2"></i> Adjust value
                              </a>
                              <a v-if="hasPermissions('transactions_view')" target="_blank"
                                 class="dropdown-item mt-2 mb-2"
                                 :href="'/purchases/transactions/details?id=' + row.item.transaction_id">
                                <i class="tim-icons icon-notes text-default mr-2"></i> View transaction
                              </a>
                              <a v-if="row.item.is_active"
                                 class="dropdown-item mt-2 mb-2" @click="sendGiftCardEmailPrompt(row.item, $event)" href="#">
                                <i class="tim-icons icon-email-85 text-default mr-2"></i> Resend delivery email
                              </a>
                              <a v-if="row.item.is_active"
                                 class="dropdown-item mt-2 mb-2" @click="downloadPDF(row.item, $event)" href="#">
                                <i class="tim-icons icon-cloud-download-93 text-default mr-2"></i> Download PDF
                              </a>
                              <a class="dropdown-item mt-2 mb-2" @click="viewActivityLog(row.item, $event)" href="#">
                                <i class="tim-icons icon-bullet-list-67 text-default mr-2"></i> View activity log
                              </a>
                              <hr v-if="hasPermissions('gift_cards_delete') || hasPermissions('gift_cards_update')">
                              <a v-if="hasPermissions('gift_cards_delete') && row.item.is_active" class="dropdown-item mt-2 mb-2" @click="deactivateGiftCardPrompt(row.item, 0, $event)" href="#">
                                <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px" class="inactive-dot mr-2"></span> Deactivate gift card
                              </a>
                              <a v-if="hasPermissions('gift_cards_update') && !row.item.is_active" class="dropdown-item mt-2 mb-2" @click="deactivateGiftCardPrompt(row.item, 1, $event)" href="#">
                                <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px" class="active-dot mr-2"></span> Activate gift card
                              </a>
                            </el-dropdown-menu>
                          </template>
                        </el-dropdown>
                      </div>
                    </div>
                  </template>
                </b-table>
                <b-pagination  @click.native="getGiftPurchases()"
                               v-model="currentPage"
                               :total-rows="totalRows"
                               :per-page="perPage"
                ></b-pagination>
              </div>
            </div>
            <!--        Large screens-->
            <div class="row d-sm-block d-none" style="word-break: break-all">
              <div class="col">
                <b-table v-if="gift_purchases.length > 0" :items="gift_purchases"
                         :fields="fields">
                  <template v-slot:cell(gift_card)="row">
                    <div class="gc-info-cell">
                      <div class="gc-code-wrapper">
                        <code class="gc-code">{{getGiftCardCode(row.item.code)}}</code>
                        <el-tooltip content="Copy code" effect="light" :open-delay="300" placement="top">
                          <base-button class="btn btn-link p-0 gc-copy-btn" icon simple size="sm" type="primary" @click.stop="copyGiftCardCode(row.item.code)">
                            <i class="tim-icons el-icon-copy-document"></i>
                          </base-button>
                        </el-tooltip>
                      </div>
                      <div class="gc-meta">
                        <span v-if="getGiftCardTypeName(row.item.gift_card_type_id)" class="badge badge-default mb-0">
                          <i class="tim-icons icon-tag"></i>{{ getGiftCardTypeName(row.item.gift_card_type_id) }}
                        </span>
                        <utm-attribution
                          v-if="enable_utm_source_tracking && row.item.utm_source"
                          icon="icon-world"
                          :source="row.item.utm_source"
                          :medium="row.item.utm_medium"
                          :campaign="row.item.utm_campaign"
                          :referrer="row.item.utm_referrer" />
                        <div v-if="row.item.is_refund_gift_card" class="gc-info-badge gc-info-badge--refund">
                          <i class="tim-icons icon-refresh-02"></i>
                          <span>Refund</span>
                        </div>
                        <el-tooltip v-if="row.item.suppress_confirmation_email" content="The purchase confirmation email to the customer was turned off at purchase." effect="light" :open-delay="300" placement="top">
                          <span class="badge badge-default mb-0">
                            <i class="tim-icons icon-email-85"></i>No confirmation email
                          </span>
                        </el-tooltip>
                        <el-tooltip v-if="row.item.suppress_delivery_email" content="The delivery email to the recipient was turned off at purchase. Use Send gift card email to deliver it manually." effect="light" :open-delay="300" placement="top">
                          <span class="badge badge-default mb-0">
                            <i class="tim-icons icon-send"></i>No delivery email
                          </span>
                        </el-tooltip>
                        <span v-if="row.item.is_active" class="gc-status gc-status--active">Active</span>
                        <span v-if="!row.item.is_active" class="gc-status gc-status--inactive">Inactive</span>
                      </div>
                    </div>
                  </template>
                  <template v-slot:cell(gift_card_code)="row">
                    <div class="gc-code-wrapper">
                      <code class="gc-code">{{getGiftCardCode(row.item.code)}}</code>
                      <el-tooltip content="Copy code" effect="light" :open-delay="300" placement="top">
                        <base-button class="btn btn-link p-0 gc-copy-btn" icon simple size="sm" type="primary" @click.stop="copyGiftCardCode(row.item.code)">
                          <i class="tim-icons el-icon-copy-document"></i>
                        </base-button>
                      </el-tooltip>
                    </div>
                    <span v-if="row.item.is_active" class="gc-status gc-status--active">Active</span>
                    <span v-if="!row.item.is_active" class="gc-status gc-status--inactive">Inactive</span>
                  </template>
                  <template v-slot:cell(select)="row">
                   <base-checkbox v-model="row.item.selected" v-on:input="selectGiftCard(row.item)"></base-checkbox>
                  </template>
                  <template #head(select)="data">
                    <div style="white-space: nowrap">
                      <base-checkbox v-model="select_all_gift_cards" v-on:input="selectAllGiftCards()"></base-checkbox>
                    </div>
                  </template>
                  <template #head(actions)="data">
                    <div style="white-space: nowrap">
                      actions
                    </div>
                  </template>
                  <template v-slot:cell(people)="row">
                    <div class="people-cell">
                      <div class="person-entry">
                        <div class="person-header">
                          <span class="person-label person-label--from">From</span>
                          <span class="person-name text-white">{{ row.item.first_name }} {{ row.item.last_name }}</span>
                          <el-tooltip content="View customer" effect="light" :open-delay="300" placement="top">
                            <base-button
                              v-if="hasPermissions('customer_view')"
                              @click="viewCustomer(row.item.customer_id)"
                              icon
                              class="btn btn-link p-0 ml-1"
                              simple
                              size="sm"
                              type="primary"
                            >
                              <i class="tim-icons el-icon-top-right"></i>
                            </base-button>
                          </el-tooltip>
                        </div>
                        <div class="person-email"><i class="tim-icons icon-email-85"></i> {{ row.item.email }}</div>
                      </div>
                      <div
                        v-for="(r, i) in getRecipients(row.item.recipients)"
                        :key="'R' + i"
                        class="person-entry person-entry--recipient"
                      >
                        <div class="person-header">
                          <span class="person-label person-label--to">To</span>
                          <span class="person-name text-white">{{ r.first_name }} {{ r.last_name }}</span>
                          <el-tooltip content="View customer" effect="light" :open-delay="300" placement="top">
                            <base-button
                              v-if="hasPermissions('customer_view')"
                              @click="viewCustomer(r.user_id)"
                              icon
                              class="btn btn-link p-0 ml-1"
                              simple
                              size="sm"
                              type="primary"
                            >
                              <i class="tim-icons el-icon-top-right"></i>
                            </base-button>
                          </el-tooltip>
                        </div>
                        <div class="person-email"><i class="tim-icons icon-email-85"></i> {{ r.email }}</div>
                        <div v-if="row.item.validate_emails" class="person-badge-row">
                          <badge type="info">validates recipient email</badge>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-slot:cell(value)="row">
                    <div class="text-white" style="font-size: small; white-space: nowrap;">Value: {{ getAmount(row.item.total) }}</div>
                    <div class="text-white" style="font-size: small; white-space: nowrap;">Price: {{ getAmount(row.item.price) }}</div>
                    <div class="text-white" style="font-size: small; white-space: nowrap;">Remaining: {{ getAmount(row.item.remaining) }}</div>
                    <el-popover v-if="hasFinancialDetails(row.item)" trigger="click" placement="right" width="220" popper-class="gc-details-popover">
                      <div>
                        <div class="gc-pop-section">Gift card</div>
                        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                          <span class="gc-pop-label">Value</span>
                          <span class="gc-pop-value">{{ getAmount(row.item.total) }}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                          <span class="gc-pop-label">Price</span>
                          <span class="gc-pop-value">{{ getAmount(row.item.price) }}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                          <span class="gc-pop-label">Remaining</span>
                          <span class="gc-pop-value">{{ getAmount(row.item.remaining) }}</span>
                        </div>
                        <div v-if="row.item.discount > 0" style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                          <span class="gc-pop-label">Discount</span>
                          <span class="gc-pop-value">-{{ getAmount(row.item.discount) }}</span>
                        </div>
                        <div v-if="row.item.fee > 0" style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                          <span class="gc-pop-label">Fee</span>
                          <span class="gc-pop-value">{{ getAmount(row.item.fee) }}</span>
                        </div>
                      </div>
                      <div class="gc-pop-divider"></div>
                      <div>
                        <div class="gc-pop-section">Transaction</div>
                        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                          <span class="gc-pop-label">Total</span>
                          <span class="gc-pop-value">{{ getAmount(row.item.transaction_total) }}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                          <span class="gc-pop-label">Paid</span>
                          <span class="gc-pop-value">{{ getAmount(row.item.transaction_paid) }}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between;">
                          <span class="gc-pop-label">Due</span>
                          <span class="gc-pop-value">{{ getAmount(row.item.transaction_due) }}</span>
                        </div>
                      </div>
                      <span slot="reference" style="font-size: 11px; color: #e14eca; cursor: pointer;">
                        More details <i class="tim-icons icon-minimal-down" style="font-size: 9px; margin-left: 2px;"></i>
                      </span>
                    </el-popover>
                  </template>
                  <template v-slot:cell(gift_card_type)="row">
                    <badge v-if="getGiftCardTypeName(row.item.gift_card_type_id)">{{ getGiftCardTypeName(row.item.gift_card_type_id) }}</badge>
                    <span v-else class="text-muted">-</span>
                    <badge v-if="row.item.is_refund_gift_card" type="warning" class="ml-1" style="font-size: 10px;">refund</badge>
                  </template>
                  <template v-slot:cell(dates)="row">
                    <div class="text-white" style="font-size: small; white-space: nowrap">Purchased: {{getLocalDate(row.item.date_purchased)}}, {{getLocalTime(row.item.date_purchased)}}</div>
                    <div class="text-white" style="font-size: small; white-space: nowrap">Delivery: {{ getDeliveryDate(row.item.delivery_date, row.item.date_purchased) }}</div>
                    <div class="text-white" style="font-size: small; white-space: nowrap">Activated:
                      <span v-if="row.item.activation_date">{{ getFormattedDate(row.item.activation_date) }}</span>
                      <span v-else>{{ getLocalDate(row.item.date_purchased) }}</span>
                    </div>
                    <div class="text-white" style="font-size: small; white-space: nowrap">Expires:
                      <badge v-if="!row.item.expiration_date">does not expire</badge>
                      <span v-if="row.item.expiration_date">{{ getFormattedDate(row.item.expiration_date) }}</span>
                    </div>
                  </template>
                  <template v-slot:cell(source)="row">
                    <span v-if="!row.item.utm_source">-</span>
                    <badge v-else>{{ capitalizeFirstLetter(row.item.utm_source) }}</badge>
                  </template>
                  <template v-slot:cell(purchase_date)="row">
                     <span style="font-size: small; white-space: nowrap">{{getLocalDate(row.item.date_purchased)}},
                       {{getLocalTime(row.item.date_purchased)}}</span>
                  </template>
                  <template v-slot:cell(delivery_date)="row">
                    <span style="font-size: small; white-space: nowrap">{{ getDeliveryDate(row.item.delivery_date, row.item.date_purchased) }}</span>
                  </template>
                  <template v-slot:cell(activation_date)="row">
                    <span v-if="row.item.activation_date" style="font-size: small; white-space: nowrap">{{ getFormattedDate(row.item.activation_date) }}</span>
                    <span v-else style="font-size: small; white-space: nowrap">{{ getLocalDate(row.item.date_purchased) }}</span>
                  </template>
                  <template v-slot:cell(expires)="row">
                    <badge v-if="!row.item.expiration_date">does not expire</badge>
                    <span style="font-size: small; white-space: nowrap" v-if="row.item.expiration_date">{{ getFormattedDate(row.item.expiration_date) }}</span>
                  </template>
                  <template v-slot:cell(actions)="row">
                    <el-dropdown>
                      <base-button
                        simple
                        type="primary"
                        link
                      > <i style="font-size: medium" class="tim-icons el-icon-more"></i>
                      </base-button>
                      <template #dropdown>
                        <el-dropdown-menu>
                          <a v-if="hasPermissions('gift_cards_update')"
                              class="dropdown-item mt-2 mb-2" @click="editGiftCardPrompt(row.item, $event)" href="#">
                            <i class="tim-icons icon-pencil text-default mr-2"></i> Edit gift card
                          </a>
                          <a v-if="hasPermissions('gift_cards_update') && row.item.is_active"
                              class="dropdown-item mt-2 mb-2" @click="openAdjustValueModal(row.item, $event)" href="#">
                            <i class="tim-icons icon-money-coins text-default mr-2"></i> Adjust value
                          </a>
                          <a  v-if="hasPermissions('transactions_view')" target="_blank"
                              class="dropdown-item mt-2 mb-2"
                              :href="'/purchases/transactions/details?id=' + row.item.transaction_id">
                            <i class="tim-icons icon-notes text-default mr-2"></i> View transaction
                          </a>
                          <a v-if="row.item.is_active"
                              class="dropdown-item mt-2 mb-2" @click="sendGiftCardEmailPrompt(row.item, $event)" href="#">
                            <i class="tim-icons icon-email-85 text-default mr-2"></i> Resend delivery email
                          </a>
                          <a v-if="row.item.is_active"
                             class="dropdown-item mt-2 mb-2" @click="downloadPDF(row.item, $event)" href="#">
                            <i class="tim-icons icon-cloud-download-93 text-default mr-2"></i> Download PDF
                          </a>
                          <a class="dropdown-item mt-2 mb-2" @click="viewActivityLog(row.item, $event)" href="#">
                            <i class="tim-icons icon-bullet-list-67 text-default mr-2"></i> View activity log
                          </a>
                          <hr v-if="hasPermissions('gift_cards_delete') || hasPermissions('gift_cards_update')">
                          <a v-if="hasPermissions('gift_cards_delete') && row.item.is_active" class="dropdown-item mt-2 mb-2" @click="deactivateGiftCardPrompt(row.item, 0, $event)" href="#">
                            <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px" class="inactive-dot mr-2"></span> Deactivate gift card
                          </a>
                          <a v-if="hasPermissions('gift_cards_update') && !row.item.is_active" class="dropdown-item mt-2 mb-2" @click="deactivateGiftCardPrompt(row.item, 1, $event)" href="#">
                            <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px" class="active-dot mr-2"></span> Activate gift card
                          </a>
                        </el-dropdown-menu>
                      </template>
                    </el-dropdown>
                  </template>
                </b-table>
                <b-pagination  @click.native="getGiftPurchases()"
                               v-model="currentPage"
                               :total-rows="totalRows"
                               :per-page="perPage"
                ></b-pagination>
              </div>
            </div>
          </div>

          <div v-if="activeTabIndex === 1">
            <div class="mt-3" v-if="gift_card_types.length <= 0">
              <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
                <h4 class="mt-3">No gift card types have been created.</h4>
              </div>
            </div>

<!--            Types-->
            <div class="row d-sm-block d-none">
              <div class="col">
                <table class='table' v-if="gift_card_types.length > 0">
                  <thead>
                  <tr>
                    <th scope='col' style="white-space: nowrap"></th>
                    <th scope='col' style="white-space: nowrap">External name</th>
                    <th scope='col' style="white-space: nowrap">Internal label</th>
                    <th scope='col' style="white-space: nowrap">Value type</th>
                    <th scope='col' style="white-space: nowrap">Internal only</th>
                    <th scope='col' style="white-space: nowrap">Activation</th>
                    <th scope='col' style="white-space: nowrap">Delivery</th>
                    <th scope='col' style="white-space: nowrap">Expiration</th>
                    <th scope='col' style="white-space: nowrap">Status</th>
                    <th scope='col' style="white-space: nowrap">Actions</th>
                  </tr>
                  </thead>
                  <tbody tag='tbody' v-model='gift_card_types' is='draggable'  @start="isDraggingGiftCardTypes = true"
                         @end="isDraggingGiftCardTypes = false">
                  <tr v-for='(item, index) in gift_card_types' :key="'GCT' + index">
                    <td scope='row'>
                      <i style="cursor: move" class="ml-3 tim-icons el-icon-rank"></i>
                    </td>
                    <td scope='row'>
                      <div>{{item.name}}</div>
                      <small>{{item.description}}</small>
                    </td>
                    <td scope='row'>
                      <div v-if="item.internal_label">{{item.internal_label}}</div>
                      <div v-else>-</div>
                    </td>
                    <td scope='row'>
                      <badge v-if="item.value_type === 0">Arbitrary value</badge>
                      <badge v-if="item.value_type === 1">Fixed value</badge>
                      <badge v-if="item.value_type === 2">Value of event</badge>
                    </td>
                    <td scope='row'>
                      <i v-if="item.internal_only" class="text-success tim-icons icon-check-2"></i>
                      <i v-if="!item.internal_only" class="text-danger tim-icons icon-simple-remove"></i>
                    </td>
                    <td scope='row'>
                      <span v-if="item.activate_immediately">Immediately after purchase</span>
                      <span v-if="!item.activate_immediately">
            <span v-if="item.activation_type === 1">{{item.activation_value}} {{getPeriodName(item.activation_period)}} after purchase</span>
            <span v-if="item.activation_type === 2">On {{getFormattedDate(item.activation_date)}}</span>
          </span>
                    </td>
                    <td scope='row'>
                      <span v-if="item.allow_customer_set_delivery_date">Set by customer</span>
                      <span v-if="!item.allow_customer_set_delivery_date">
            <span v-if="item.delivery_type === 0">Immediately after purchase</span>
            <span v-if="item.delivery_type === 1">{{item.delivery_value}} {{getPeriodName(item.delivery_period)}} after purchase</span>
            <span v-if="item.delivery_type === 2">On {{getFormattedDate(item.delivery_date)}}</span>
          </span>
                    </td>
                    <td scope='row'>
                      <span v-if="!item.has_expiration_date">Does not expire</span>
                      <span v-if="item.has_expiration_date">
            <span v-if="item.expiration_type === 1">{{item.expiration_value}} {{getPeriodName(item.expiration_period)}} after purchase</span>
            <span v-if="item.expiration_type === 2">On {{getFormattedDate(item.expiration_date)}}</span>
          </span>
                    </td>
                    <td scope='row'>
                      <badge v-if="item.active" type="success">active</badge>
                      <badge v-if="!item.active" type="danger">deactivated</badge>
                    </td>
                    <td scope='row'>
                      <div class="row">
                        <el-dropdown>
                          <base-button link type="primary" size="sm">
                            <i style="font-size: large" class="tim-icons el-icon-more"></i>
                          </base-button>
                          <template #dropdown>
                            <el-dropdown-menu>
                              <a class="dropdown-item mt-2 mb-2" @click="editGiftCardType(item, $event)" href="#">
                                <i class="tim-icons icon-pencil text-warning mr-2"></i> Edit gift card type
                              </a>
                              <a class="dropdown-item" v-if="item.active" @click="activateGiftCardTypePrompt(item, $event, 0)" href="#">
                                <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px" class="inactive-dot mr-2"></span> Deactivate gift card type
                              </a>
                              <a class="dropdown-item" v-if="!item.active" @click="activateGiftCardTypePrompt(item, $event, 1)" href="#">
                                <span style="padding-left: 0px; padding-bottom: 0px; margin-left: -3px; margin-bottom: -3px" class="active-dot mr-2"></span> Activate gift card type
                              </a>
                              <hr>
                              <a class="dropdown-item mt-2 mb-2" @click="archiveGiftCardTypePrompt(item, $event)" href="#">
                                <i class="tim-icons icon-trash-simple text-danger mr-2"></i> Archive gift card type
                              </a>
                            </el-dropdown-menu>
                          </template>
                        </el-dropdown>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="row d-sm-none d-block">
              <div class="col">
                <b-table v-if="gift_card_types.length > 0" :items="gift_card_types"
                         :fields="['gift_card_types']">
                  <template v-slot:head(gift_card_types)="row">
                    <span>Total results: {{gift_card_types.length}}</span>
                  </template>
                  <template v-slot:cell(gift_card_types)="row">
                    <div class="row justify-content-between">
                      <div class="col">
                        <div>{{row.item.name}}</div>
                        <small>{{row.item.description}}</small>
                      </div>
                      <div class="col-4 text-right">
                        <badge style="font-size: 8px; padding-left: 7px; padding-right: 7px" v-if="row.item.value_type === 0">Arbitrary value</badge>
                        <badge style="font-size: 8px; padding-left: 7px; padding-right: 7px" v-if="row.item.value_type === 1">Fixed value</badge>
                        <badge style="font-size: 8px; padding-left: 7px; padding-right: 7px" v-if="row.item.value_type === 2">Value of event</badge>
                      </div>
                    </div>
                    <div v-if="row.item.internal_label">{{row.item.internal_label}}</div>

                    <div class="row mt-2">
                      <div class="col">
                        <div
                          class="btn-group btn-group-toggle mt-1 mb-2"
                          :class="'float-left'"
                          data-toggle="buttons" style="width:100%"
                        >
                          <label class="btn btn-sm btn-primary btn-simple"
                                 style="padding-right: 0; padding-left: 0">
                            <base-button
                              @click.native="archiveGiftCardTypePrompt(row.item, $event)"
                              class="like btn-link"
                              type="primary"
                              size="sm"
                              icon
                            ><i class="tim-icons icon-trash-simple"></i>
                            </base-button>
                          </label>
                          <label class="btn btn-sm btn-primary btn-simple" style="padding-right: 0; padding-left: 0">
                            <base-button
                              @click.native="editGiftCardType(row.item, $event)"
                              class="like btn-link" type="primary"
                              size="sm"
                              icon
                            ><i class="tim-icons icon-pencil"></i>
                            </base-button>
                          </label>
                        </div>
                      </div>
                    </div>
                  </template>
                </b-table>
              </div>
            </div>
          </div>


          <div v-if="activeTabIndex === 2">
            <div class="row">
              <div class="col-lg-4">
                <link v-if="!loading" rel="stylesheet" :href="'https://fonts.googleapis.com/css?family=' + booking_site_settings.font_family_card">
                <card :style="{ 'background-color': booking_site_settings.card_color}">
                  <div slot="image" class="card-img-top" style="padding-bottom: 0; height: 300px">
                    <div :style="{'background-color': gift_card_settings.background_color}" style="border-radius: 5px; width: 100%; height: 300px; position: relative">
                      <img
                        :style="{'transform': 'translate(0%, 0%) scale(' + gift_card_settings.scale*0.01 + ')',
                      'left': gift_card_settings.x + 'px', 'top': gift_card_settings.y + 'px', 'object-fit':  gift_card_settings.object_fit, 'height': '100%'}"
                        class="card-img"
                        style="position: absolute; transform-origin: center center; will-change: transform"
                        slot="image" :src="gift_card_settings.url"  alt="Card image cap"/>
                    </div>
                  </div>


                  <div class="row">
                    <div class="col" :style="{ 'font-family': booking_site_settings.font_family_card,
                                  'border-color': booking_site_settings.button_color,
                                  'color': booking_site_settings.button_color }">
                      <h4 :style="{'font-family': booking_site_settings.font_family_card, 'color': booking_site_settings.text_color_card}">
                        {{ gift_card_settings.title }}
                      </h4>
                      <p :style="{'font-family': booking_site_settings.font_family_card,
                       'color': booking_site_settings.text_color_card}">{{ gift_card_settings.subtext }}</p>
                    </div>
                  </div>
                  <div class="row mt-3">
                    <div class="col" :style="{ 'font-family': booking_site_settings.font_family_card,
                                  'border-color': booking_site_settings.button_color,
                                  'color': booking_site_settings.button_color }">
                      <base-button :style="{ 'font-family': booking_site_settings.font_family_card,
                                  'border-color': booking_site_settings.button_color,
                                  'color': booking_site_settings.button_color, 'width': '100%', 'padding-left': '3px',  'padding-right': '3px' }"
                                   simple disabled>
                        {{ gift_card_settings.button_text }}
                      </base-button>
                    </div>
                  </div>
                </card>
              </div>
              <div class="col-lg-4">
                <div class="mb-2">
                  <image-editor text="Upload a new header image" @saveImage="saveImage"></image-editor>
                </div>
                <label>
                  <div>Card title
                    <el-popover trigger="hover"
                                placement="top">

                      <div>
                        <div class="popover-body">This text will appear as the title on the booking site's gift card section</div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </div>
                </label>

                <base-input v-model="gift_card_settings.title" placeholder="Title"></base-input>

                <label>
                  <div>Card text
                    <el-popover trigger="hover"
                                placement="top">

                      <div>
                        <div class="popover-body">This text will appear as the subtext on the booking site's gift card section</div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </div>
                </label>

                <base-input  v-model="gift_card_settings.subtext" placeholder="Card text"></base-input>

                <label>
                  <div>Button text
                    <el-popover trigger="hover"
                                placement="top">

                      <div>
                        <div class="popover-body">This text will appear on the button to select gift card purchase</div>
                      </div>
                      <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                    </el-popover>
                  </div>
                </label>

                <base-input  v-model="gift_card_settings.button_text" placeholder="Card text"></base-input>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-4">
                <base-button type="primary" :disabled="loading" v-on:click="saveGiftCardSettings()">Save</base-button>
              </div>
            </div>

          </div>
        </card>
      </div>
    </div>

    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="false"
      :paginate-elements-by-height="1400"
      filename="gift_card"
      :pdf-quality="2"
      :manual-pagination="false"
      pdf-format="a4"
      pdf-orientation="portrait"

      @beforeDownload="beforeDownload($event)"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <h1>hello</h1>
        <div class="row justify-content-center">
          <div class="col text-center">
            <h3 class="text-default">Your gift card from {{ group.name }} is here!</h3>
          </div>
          <!--        <p style="font-size: large" class="text-primary"> Gift card </p>-->
          <!--        <p class="text-black-50">{{selectedGiftCard}}</p>-->
          <!--&lt;!&ndash;        <img crossorigin="*" style="object-fit: cover; height: 75px" :src="selectedGiftCard.design_url"/>&ndash;&gt;-->
          <!--        <img style="object-fit: cover; height: 75px" :src="gift_card_img"/>-->

          <!--&lt;!&ndash;        <p class="text-black-50">{{ gift_card_img }}</p>&ndash;&gt;-->
          <!--        <i class="tim-icons icon-cloud-download-93 text-primary"></i>-->


        </div>
        <div class="row justify-content-center" style="background-color: #e3e3e3">
          <div class="col text-center" >
            <img style="object-fit: cover; height: 250px" :src="gift_card_img"/>
          </div>
        </div>

        <div class="row justify-content-center">
          <div class="col text-center" >
            <div style="font-size: xxx-large" class="mt-4 text-default">{{getAmount(selectedGiftCard.total)}}</div>
            <div style="font-size: x-small" class="text-black-50">Current balance</div>
            <div v-if="getGiftCardExpirationDisplay(selectedGiftCard)" style="font-size: x-small" class="mt-2 text-black-50">{{ getGiftCardExpirationDisplay(selectedGiftCard) }}</div>
            <p class="mt-3 text-black-50">{{selectedGiftCard.personal_message}} </p>
          </div>
        </div>

        <hr class="mt-3">
        <div class="row justify-content-center">
          <div class="col-lg-8 text-center" >
            <div style="font-size: x-large" class=" mt-3 text-default">Gift card code</div>
            <small class="text-black-50" style="margin-top: -10px">Provide the gift card code at checkout to redeem the gift card.</small>
            <div style="background-color: #e3e3e3; border-radius: 5px; margin-top: 20px; padding-top: 50px; padding-bottom: 50px">{{ getGiftCardCode(selectedGiftCard.code) }}</div>
          </div>
        </div>
        <hr class="mt-5">
        <div class="row justify-content-center">
          <div class="col text-center" >
            <img style="object-fit: cover; height: 50px" :src="logo"/>
            <p class="text-black-50">{{group.name}}</p>
            <p class="text-black-50">{{group.address_line_1}} {{group.address_line_1}} {{group.city}} {{group.state}} {{group.zip}}</p>
          </div>
        </div>
      </section>
    </vue-html2pdf>

    <!--    Edit GC-->
    <modal :show.sync="modals.edit_gc"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4>Edit gift card</h4>

        <div class="row">
          <div class="col">
            <label>Gift card code</label>
            <p class="text-white">{{getGiftCardCode(selectedGiftCard.code)}}</p>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-lg-6">
            <base-input label="Gift card type">
              <el-select
                v-model="selectedGiftCard.gift_card_type_id"
                class="select-primary"
                placeholder="Select gift card type"
              >
                <el-option
                  v-for="(gct, index) in editGiftCardTypeOptions"
                  class="select-primary"
                  :label="gct.name + (gct.active === 0 ? ' (deactivated)' : '') + (gct.internal_label ? ' (' + gct.internal_label + ')' : '')"
                  :value="gct.id"
                  :key="'EDIT_GCT' + index"
                >
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span>{{ gct.name }}</span>
                    <div style="display: flex; gap: 4px;">
                      <badge v-if="gct.active === 0" type="warning">Deactivated</badge>
                      <badge v-if="gct.value_type === 0" type="default">Arbitrary</badge>
                      <badge v-if="gct.value_type === 1" type="default">Fixed</badge>
                      <badge v-if="gct.value_type === 2" type="default">Event</badge>
                    </div>
                  </div>
                </el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <div class="row mb-3" v-if="giftCardTypeChangeWarning">
          <div class="col">
            <div class="alert alert-warning" role="alert" style="margin-bottom: 0">
              <i class="tim-icons icon-alert-circle-exc mr-2"></i>
              <span v-html="giftCardTypeChangeWarning"></span>
            </div>
          </div>
        </div>

        <label>Recipient</label>
        <div class="row" :key="j + 'R'">
          <div class="col-12 col-lg-6">
            <base-input :error="errs.recipient_first_name" v-on:change="j++"
                        v-model="selectedGiftCard.recipient_first_name"
                        placeholder="Recipient's first name"></base-input>
          </div>
          <div class="col-12 col-lg-6" >
            <base-input :error="errs.recipient_last_name"  v-on:change="j++"
                        v-model="selectedGiftCard.recipient_last_name"
                        placeholder="Recipient's last name"></base-input>
          </div>
        </div>
        <div class="row" :key="j + 'RE'">
          <div class="col">
            <base-input :error="errs.recipient_email"  v-on:change="j++"
                        v-model="selectedGiftCard.recipient_email" placeholder="Recipient's email"></base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <base-input label="Activation date" :error="errs.activation_date">
              <el-date-picker :editable="false"
                type="date"
                placeholder="Select date"
                :format="day_of_week + date_format_cal"
                v-model="selectedGiftCard.activation_date" :picker-options="{ firstDayOfWeek: first_day_of_week }"
              >
              </el-date-picker>
            </base-input>
          </div>
          <div class="col-lg-6">
            <base-input label="Expiration date" :error="errs.expiration_date">
              <el-date-picker :editable="false"
                type="date"
                placeholder="Select date"
                :format="day_of_week + date_format_cal"
                v-model="selectedGiftCard.expiration_date" :picker-options="{ firstDayOfWeek: first_day_of_week }"
              >
              </el-date-picker>
            </base-input>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col text-center">
            <base-button type="default" v-on:click="modals.edit_gc = false" class="mr-2">Cancel</base-button>
            <base-button type="primary" :disabled="loading" v-on:click="saveGiftCard()">Save</base-button>
          </div>
        </div>

      </card>
    </modal>


    <!--    Bulk actions-->
    <modal :show.sync="modals.bulk_update"
           body-classes="p-0"
           modal-classes="modal-dialog-centered modal-lg">
      <card type="secondary"
            header-classes="bg-white pb-5"
            body-classes="px-lg-5 py-lg-5"
            class="border-0 mb-0">

        <h4>Bulk update</h4>

        <div class="row">
          <div class="col">
            <p class="text-uppercase card-title">Gift cards to bulk update</p>
            <div v-if="!select_all_gift_cards">
              <span v-for="(gc, i) in selectedGiftCards" :key="'SGC' + i">
                <span class="ml-1" v-if="i>0"></span><badge>{{getGiftCardCode(gc.code)}}</badge>
              </span>
            </div>
            <div v-else>
              <badge>{{totalRows}} gift cards</badge>
            </div>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col">
            <p class="text-uppercase card-title">Bulk actions</p>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <el-switch active-color="#e14eca"
                       v-model="validate_recipient_email"  />
            <label> &nbsp; Validate recipient email</label>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col">
            <el-switch active-color="#e14eca"
                       v-model="bulk_change_gift_card_type"  />
            <label> &nbsp; Change gift card type</label>
          </div>
        </div>

        <div class="row mt-2" v-if="bulk_change_gift_card_type">
          <div class="col-lg-8">
            <base-input label="New gift card type">
              <el-select
                v-model="bulk_new_gift_card_type_id"
                class="select-primary"
                placeholder="Select gift card type"
              >
                <el-option
                  v-for="(gct, index) in activeGiftCardTypes"
                  class="select-primary"
                  :label="gct.name + (gct.internal_label ? ' (' + gct.internal_label + ')' : '')"
                  :value="gct.id"
                  :key="'BULK_GCT' + index"
                >
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span>{{ gct.name }}</span>
                    <badge v-if="gct.value_type === 0" type="default">Arbitrary</badge>
                    <badge v-if="gct.value_type === 1" type="default">Fixed</badge>
                    <badge v-if="gct.value_type === 2" type="default">Event</badge>
                  </div>
                </el-option>
              </el-select>
            </base-input>
          </div>
        </div>

        <div class="row mb-3" v-if="bulk_change_gift_card_type && bulk_new_gift_card_type_id">
          <div class="col">
            <div class="alert alert-warning" role="alert" style="margin-bottom: 0">
              <i class="tim-icons icon-alert-circle-exc mr-2"></i>
              <strong>Note:</strong> Changing the gift card type will update the type association but will not modify existing values, remaining balances, or expiration dates.
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col text-center">
            <base-button type="default" v-on:click="modals.bulk_update = false" class="mr-2">Cancel</base-button>
            <base-button type="primary" :disabled="loading || (bulk_change_gift_card_type && !bulk_new_gift_card_type_id)" v-on:click="bulkUpdate()">Save</base-button>
          </div>
        </div>

      </card>
    </modal>

        <!--    GC purchase-->
    <gift-card-purchase-drawer
      :show="modals.new"
      :gift-card-types="activeGiftCardTypes"
      :games="games"
      :fees="fees"
      :payment-settings="settings"
      @close="modals.new = false"
      @created="getGiftPurchases()"
    />

    <base-alert-dialog
      :show="confirmationDialog.show"
      :title="confirmationDialog.title"
      :message="confirmationDialog.message"
      :variant="confirmationDialog.variant"
      :show-cancel="confirmationDialog.showCancel"
      :confirm-text="confirmationDialog.confirmText"
      :confirm-variant="confirmationDialog.confirmVariant"
      :dismissible="confirmationDialog.dismissible"
      @confirm="confirmationDialogConfirm"
      @cancel="confirmationDialog.show = false"
    />

    <!--    Activity log-->
    <gift-card-activity-log-modal
      :show.sync="modals.activity_log"
      :activity-log="activity_log"
      :gift-card-code="selected_gift_card_for_log ? getGiftCardCode(selected_gift_card_for_log.code) : ''"
      :loading="activity_log_loading"
      :date-format="date_format"
      :time-format="time_format"
      :currency-symbol="currency_symbol"
      :currency="currency"
      :currency-format="currency_format"
      :number-format="number_format"
      :has-transaction-permission="hasPermissions('transactions_view')"
      @close="modals.activity_log = false"
    />

    <!--    Adjust value-->
    <gift-card-adjust-value-modal
      :show.sync="modals.adjust_value"
      :gift-card="selectedGiftCardForAdjustment"
      :currency-symbol="currency_symbol"
      :currency="currency"
      :currency-format="currency_format"
      :number-format="number_format"
      @close="modals.adjust_value = false"
      @adjusted="onGiftCardAdjusted"
    />

  </div>
</template>

<script>
import {Auth} from "aws-amplify";
import currencies from "@/assets/currencies.json";
import {API_LOCATION} from "@/api-config";
import {BTable, BPagination} from 'bootstrap-vue'
import {dayOfWeek, formattedDate, time} from "@/plugins/dateFormatter";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";
import axios from "axios";
import {Badge, BaseAlert} from '@/components';
import VueHtml2pdf from 'vue-html2pdf'
import {Switch, Dropdown, DropdownMenu, DropdownItem} from "element-ui";
import _ from "lodash";
import countries from "@/assets/countries.json";
import ImageEditor from "@/components/ImageEditor";
import moment from "moment"
import {isValidEmail} from "@/util/emailValidator";
import draggable from 'vuedraggable'
import GiftCardActivityLogModal from './components/GiftCardActivityLogModal.vue'
import GiftCardAdjustValueModal from './components/GiftCardAdjustValueModal.vue'
import GiftCardPurchaseDrawer from './components/GiftCardPurchaseDrawer.vue'
import posPaymentService from '@/services/posPayment'
import TabRail from '@/components/TabRail'

export default {
  name: "GiftCards",
  components: {
    ImageEditor,
    BTable,
    BPagination,
    Badge,
    VueHtml2pdf,
    [Switch.name]: Switch,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    BaseAlert,
    draggable,
    GiftCardActivityLogModal,
    GiftCardAdjustValueModal,
    GiftCardPurchaseDrawer,
    TabRail
  },
  data() {
    return {
      first_day_of_week: JSON.parse(localStorage.getItem('group')).first_day_of_week || 7,
      fields: [
        'select',
        { key: 'gift_card_code', label: 'Code' },
        { key: 'gift_card_type', label: 'Type' },
        { key: 'people', label: 'People' },
        { key: 'value', label: 'Value' },
        { key: 'dates', label: 'Dates' },
        'actions'
      ],
      enable_utm_source_tracking: 0,
      booking_site_settings: '',
      gift_card_settings: {
        title: '',
        image_id: '',
        subtext: '',
        button_text: ''
      },
      games: [],
      settings: '',
      modals: {
        new: false,
        edit_gc: false,
        bulk_update: false,
        activity_log: false,
        adjust_value: false
      },
      selectedGiftCardForAdjustment: null,
      activity_log: [],
      activity_log_loading: false,
      selected_gift_card_for_log: null,
      selectedGiftCard: '',
      gift_card_img: '',
      logo: '',
      country: '',
      countries: countries.countries,
      currencies: currencies.currencies,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      currency_symbol: '$',
      currency: JSON.parse(localStorage.getItem('group')).currency,
      currency_format: JSON.parse(localStorage.getItem('group')).currency_format,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      date_format_cal: '',
      day_of_week: '',
      group: JSON.parse(localStorage.getItem('group')),
      gift_purchases: [],
      loading: false,
      perPageOptions: [5, 10, 25, 50],
      currentPage: 1,
      totalRows: 0,
      perPage: 10,
      // SavedViewsMenu drives the first fetch via @change; this guards it.
      didInitialFetch: false,
      // Flat filter values for the shared <filter-bar>. Seeded from the URL;
      // saved-views precedence is resolved by FilterBar. gift_card_type = type id.
      filterValues: {
        code: '',
        customer: '',
        recipient: '',
        status: '',
        gift_card_type: ''
      },
      fees: [],
      errs: {
        recipient_first_name: '',
        recipient_last_name: '',
        recipient_email: '',
        custom_code: '',
        value: '',
        activation_date: '',
        expiration_date: ''
      },
      j: 0,
      activeTabIndex: 0,
      confirmationDialog: {
        show: false,
        title: '',
        message: '',
        variant: 'warning',
        showCancel: false,
        confirmText: 'OK',
        confirmVariant: 'primary',
        dismissible: true,
        onConfirm: null
      },
      gift_card_types: [],
      selectedGiftCards: [],
      select_all_gift_cards: false,
      validate_recipient_email: false,
      bulk_change_gift_card_type: false,
      bulk_new_gift_card_type_id: null,
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      isDraggingGiftCardTypes: false,
      periods: [
        {id: 0, name: 'minute(s)'},
        {id: 1, name: 'hour(s)'},
        {id: 2, name: 'day(s)'},
        {id: 3, name: 'week(s)'},
        {id: 4, name: 'month(s)'},
        {id: 5, name: 'years(s)'}
      ],
    }
  },
  computed: {
    posSessionStatus(){
      const session = posPaymentService.state.activeSession
      return session ? session.status : null
    },
    categories() {
      return [
        {name: 'Gift card purchases', shortName: 'Purchases'},
        {name: 'Gift card types', shortName: 'Types'},
        {name: 'Gift card settings', shortName: 'Settings'}
      ];
    },
    // Schema for the shared <filter-bar>: text fields + single-select status/type.
    filterSchema() {
      return [
        { key: 'code', type: 'text', label: 'Gift card code', placeholder: 'Code', operatorLabel: 'contains', primary: true },
        { key: 'customer', type: 'text', label: 'Customer', placeholder: 'Customer name or email', operatorLabel: 'contains', primary: true },
        { key: 'recipient', type: 'text', label: 'Recipient', placeholder: 'Recipient name or email', operatorLabel: 'contains', primary: true },
        { key: 'status', type: 'select', label: 'Status', primary: true,
          options: [ { label: 'Active', value: 'active' }, { label: 'Deactivated', value: 'deactivated' } ] },
        { key: 'gift_card_type', type: 'select', label: 'Type', primary: true,
          options: (Array.isArray(this.gift_card_types) ? this.gift_card_types : []).map(t => ({ label: t.name + (t.active === 0 ? ' (deactivated)' : ''), value: t.id })) }
      ]
    },
    activeGiftCardTypes() {
      return this.gift_card_types.filter(t => t.active === 1)
    },
    editGiftCardTypeOptions() {
      const currentTypeId = this.selectedGiftCard ? this.selectedGiftCard.gift_card_type_id : null
      return this.gift_card_types.filter(t => t.active === 1 || t.id === currentTypeId)
    },
    giftCardTypeChangeWarning() {
      if (!this.selectedGiftCard || !this.selectedGiftCard.original_gift_card_type_id ||
          this.selectedGiftCard.gift_card_type_id === this.selectedGiftCard.original_gift_card_type_id) {
        return null
      }

      const newType = this.gift_card_types.find(t => t.id === this.selectedGiftCard.gift_card_type_id)
      const originalType = this.gift_card_types.find(t => t.id === this.selectedGiftCard.original_gift_card_type_id)

      if (!newType || !originalType) return null

      let warnings = []

      if (newType.value_type !== originalType.value_type) {
        warnings.push('The new type uses a different value calculation method.')
      }

      if (newType.has_expiration_date && !originalType.has_expiration_date) {
        warnings.push('The new type has expiration enabled.')
      }

      if (newType.validate_emails && !originalType.validate_emails) {
        warnings.push('The new type validates recipient emails.')
      }

      return warnings.length > 0 ? warnings.join('<br>') : null
    },
  },
  watch: {
    // OTC-3111: a Send to POS gift card is hidden until the customer pays on
    // the device. When the in-progress POS session reports paid, the card
    // becomes visible, so refresh the list.
    posSessionStatus(status, previous){
      // Refresh on paid and again when the paid session dismisses: the poll can
      // see the balance reach zero a moment before the card is activated.
      if(status === 'paid' || (previous === 'paid' && status === null)){
        this.getGiftPurchases()
      }
    },
    gift_card_types: {
      handler(newValue, oldValue) {
        if (!this.isDraggingGiftCardTypes) {
          return;
        }
        if (!this.isArrayOrderSame(newValue, oldValue)) {
          this.updateGiftCardTypesOrder();
        }
      },
      deep: true
    }
  },
  methods: {
    // FilterBar change: apply new values and (re)fetch. The first change is
    // FilterBar's initial saved-views resolution and drives the first load.
    onFiltersChanged(values){
      this.filterValues = {
        code: values.code || '',
        customer: values.customer || '',
        recipient: values.recipient || '',
        status: values.status || '',
        gift_card_type: values.gift_card_type || ''
      }
      if(!this.didInitialFetch){
        this.didInitialFetch = true
        this.getGiftPurchases()
      } else {
        // Applying a filter/view lands on page 1; reset before computing the offset
        // (getGiftPurchases with 'filter' also resets, but do it here for safety).
        this.currentPage = 1
        this.getGiftPurchases('filter')
      }
    },
    onPerPageChange(n){
      this.perPage = n
      this.currentPage = 1
      this.getGiftPurchases('filter')
    },
    hasFinancialDetails(item) {
      return item.fee > 0 || item.discount > 0 || (item.transaction_paid !== null && item.transaction_paid !== undefined)
    },
    isArrayOrderSame(newArray, oldArray) {
      if (newArray.length !== oldArray.length) {
        return false
      }
      for (let i = 0; i < newArray.length; i++) {
        if (newArray[i] !== oldArray[i]) {
          return false;
        }
      }
      return true;
    },

    async updateGiftCardTypesOrder(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gift-card-types'
      let data = {
        gift_card_types: this.gift_card_types
      }
      axios.put(url, data, config)
        .then(response => {
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', () => this.$router.push('/'))
          }
          else if(err.response.status == 500){
            this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', () => this.$router.push('/'))
          }
          else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    capitalizeFirstLetter(string){
      if (!string) {
        return string;
      }
      string = string.replace(/_/g, ' ');
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    async bulkUpdate(){
      this.loading = true
      const token = await this.getAuthToken()
      const config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gifts'

      let data = {
        validate_emails: this.validate_recipient_email,
        all_gift_cards: this.select_all_gift_cards,
        gift_cards: this.selectedGiftCards,
        gift_card_type_id: this.bulk_change_gift_card_type ? this.bulk_new_gift_card_type_id : null,
        filters: {
          code: this.filterValues.code,
          status: this.filterValues.status,
          customer: this.filterValues.customer,
          recipient: this.filterValues.recipient,
          gift_card_type: this.filterValues.gift_card_type
        }
      }

      axios.put(url, data, config)
        .then(response => {
          this.showAlert('Success', "Gift cards have been updated", 'success')
          this.modals.bulk_update = false
          this.select_all_gift_cards = false
          this.selectedGiftCards = []
          this.bulk_change_gift_card_type = false
          this.bulk_new_gift_card_type_id = null
          this.getGiftPurchases()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', () => this.$router.push('/'))
          }
          else if(err.response.status == 500){
            this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', () => this.$router.push('/'))
          }
          else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    selectAllGiftCards(){
      for(let n in this.gift_purchases){
        this.gift_purchases[n].selected = this.select_all_gift_cards
      }
      this.selectedGiftCards = []
    },
    bulkUpdatePrompt(){
      this.validate_recipient_email = false
      this.bulk_change_gift_card_type = false
      this.bulk_new_gift_card_type_id = null
      this.modals.bulk_update = true
    },
    selectGiftCard(gc){
      if(gc.selected){
        this.selectedGiftCards.push({id: gc.id, code: gc.code})
      } else {
        this.selectedGiftCards =  this.selectedGiftCards.filter(item => item.id !== gc.id);
      }
    },
    isEmailValid(emailString) {
      return isValidEmail(emailString);
    },
    validateGiftCardFields(){
      let errors = 0
      if(!this.selectedGiftCard.activation_date ||
        !(moment(this.selectedGiftCard.activation_date, 'YYYY-MM-DD').isValid())){
        this.errs.activation_date = "Activation date cannot be empty"
        errors++
      } else {
        this.errs.activation_date = ""
      }

      if(!this.selectedGiftCard.recipient_first_name){
        this.errs.recipient_first_name = "First name cannot be empty"
        errors++
      } else {
        this.errs.recipient_first_name = ""
      }

      if(!this.selectedGiftCard.recipient_last_name){
        this.errs.recipient_last_name = "Last name cannot be empty"
        errors++
      } else {
        this.errs.recipient_last_name = ""
      }

      if(!this.isEmailValid(this.selectedGiftCard.recipient_email)){
        this.errs.recipient_email = "Invalid email"
        errors++
      } else {
        this.errs.recipient_email = ""
      }

      return  errors > 0 ? false : true
    },
    async saveGiftCard(){
      if(!this.validateGiftCardFields()){
        return
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gifts/' + this.selectedGiftCard.id

      let data = {
        activation_date: moment(this.selectedGiftCard.activation_date, 'YYYY-MM-DD').format('YYYY-MM-DD'),
        expiration_date: moment(this.selectedGiftCard.expiration_date, 'YYYY-MM-DD').isValid()
          ? moment(this.selectedGiftCard.expiration_date, 'YYYY-MM-DD').format('YYYY-MM-DD')
          : null,
        recipient_first_name: this.selectedGiftCard.recipient_first_name,
        recipient_last_name: this.selectedGiftCard.recipient_last_name,
        recipient_email: this.selectedGiftCard.recipient_email,
        gift_card_type_id: this.selectedGiftCard.gift_card_type_id
      }

      axios.put(url, data, config)
        .then(response => {
          this.showAlert('Success', "Gift card has been updated", 'success')
          this.modals.edit_gc = false
          this.getGiftPurchases()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', () => this.$router.push('/'))
          }
          else if(err.response.status == 500){
            this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', () => this.$router.push('/'))
          }
          else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    openAdjustValueModal(item, event) {
      if (event) event.preventDefault()
      this.selectedGiftCardForAdjustment = { ...item }
      this.modals.adjust_value = true
    },
    onGiftCardAdjusted() {
      this.getGiftPurchases()
    },
    editGiftCardPrompt(item, event){
      event.preventDefault()
      this.selectedGiftCard = {...item}
      let recipients = JSON.parse(item.recipients)

      this.selectedGiftCard.recipient_first_name = recipients.length > 0 ? recipients[0].first_name : ''
      this.selectedGiftCard.recipient_last_name = recipients.length > 0 ? recipients[0].last_name : ''
      this.selectedGiftCard.recipient_email = recipients.length > 0 ? recipients[0].email : ''

      // Store original gift card type for comparison
      this.selectedGiftCard.original_gift_card_type_id = item.gift_card_type_id

      if(this.selectedGiftCard.activation_date){
        this.selectedGiftCard.activation_date = moment(this.selectedGiftCard.activation_date, 'YYYY-MM-DD')
      } else {
        const tz = JSON.parse(localStorage.getItem('group')).tz_name
        this.selectedGiftCard.activation_date = tz
          ? moment.utc(this.selectedGiftCard.date_purchased).tz(tz).format('YYYY-MM-DD')
          : moment(this.selectedGiftCard.date_purchased).format('YYYY-MM-DD')
      }
      this.selectedGiftCard.expiration_date = moment(this.selectedGiftCard.expiration_date, 'YYYY-MM-DD').isValid()
        ? moment(this.selectedGiftCard.expiration_date, 'YYYY-MM-DD') : null
      this.modals.edit_gc = true
    },
    async viewActivityLog(item, event) {
      if (event) {
        event.preventDefault()
      }
      this.selected_gift_card_for_log = item
      this.activity_log = []
      this.activity_log_loading = true
      this.modals.activity_log = true

      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gifts/' + item.id + '/activity-log'

        const response = await axios.get(url, config)
        this.activity_log = response.data
      } catch (err) {
        console.log(err)
        if (err.response && err.response.status === 401) {
          this.$notify({
            type: 'danger',
            message: 'You do not have access to this feature.',
            icon: 'tim-icons icon-alert-circle-exc'
          })
        } else {
          this.$notify({
            type: 'danger',
            message: 'Failed to load activity log. Please try again.',
            icon: 'tim-icons icon-alert-circle-exc'
          })
        }
      } finally {
        this.activity_log_loading = false
      }
    },
    async saveImage(value){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/images'

      let data = {
        url: value.url,
        scale: value.scale,
        description: value.description,
        background_color: value.background_color,
        x: value.x,
        y: value.y,
        is_primary: 0,
        object_fit: value.object_fit
      }

      axios.post(url, data, config)
        .then(response => {
          this.loading = false
          this.gift_card_settings.image_id = response.data
          this.gift_card_settings.url = value.url
          this.gift_card_settings.x = value.x
          this.gift_card_settings.y = value.y
          this.gift_card_settings.scale = value.scale
          this.gift_card_settings.object_fit = value.object_fit
          this.gift_card_settings.background_color = value.background_color
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', () => this.$router.push('/'))
          }
          else if(err.response.status == 500){
            this.showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', () => this.$router.push('/'))
          }
          else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    getPeriodName(period_id){
      let period = _.find(this.periods, {id: period_id})
      return period ? period.name : ''
    },
    activateGiftCardTypePrompt(item, event, action){
      event.preventDefault()
      let action_type = 'activated and will immediately appear on the booking site'
      if(action === 0){
        action_type = 'permanently deactivated and will no longer be available on the booking site'
      }

      if(!item.internal_only){
        this.showConfirm({
          title: 'Are you sure?',
          message: 'The gift card type will be ' + action_type,
          onConfirm: () => this.activateGiftCardType(item, action, action_type)
        })
      } else {
        action_type = 'activated'
        if(action === 0){
          action_type = 'deactivated'
        }
        this.activateGiftCardType(item, action, action_type)
      }
    },
    async activateGiftCardType(item, action, action_type){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gift-card-types/' + item.id + '/activate'
      this.loading = true
      let data = {
        action: action
      }
      this.axios.put(url, data, config)
        .then(response => {
          this.showAlert('Success', 'Gift card type has been '  + action_type, 'success')
          this.getGiftCardTypes()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    archiveGiftCardTypePrompt(item, event){
      event.preventDefault()
      this.showConfirm({
        title: 'Are you sure?',
        message: 'The gift card type will be permanently archived and will no longer be available on the booking site',
        variant: 'danger',
        confirmVariant: 'danger',
        confirmText: 'Archive',
        onConfirm: () => this.archiveGiftCardType(item)
      })
    },
    async archiveGiftCardType(item){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gift-card-types/' + item.id
      this.loading = true
      this.axios.delete(url, config)
        .then(response => {
          this.showAlert('Success', 'Gift card type has been archived', 'success')
          this.getGiftCardTypes()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    editGiftCardType(item, event){
      event.preventDefault()
      this.$router.push({ path: '/purchases/gift-cards/settings', query: { id:  item.id }})
    },
    newGiftCardType(){
      this.$router.push('/purchases/gift-cards/settings')
    },
    showAlert(title, message, variant = 'info', onConfirm = null){
      this.confirmationDialog = {
        show: true, title, message, variant,
        showCancel: false, confirmText: 'OK', confirmVariant: 'primary', dismissible: true, onConfirm
      }
    },
    showConfirm({ title, message, variant = 'warning', confirmText = 'Confirm', confirmVariant = 'primary', onConfirm }){
      this.confirmationDialog = {
        show: true, title, message, variant,
        showCancel: true, confirmText, confirmVariant, dismissible: true, onConfirm
      }
    },
    confirmationDialogConfirm(){
      const action = this.confirmationDialog.onConfirm
      this.confirmationDialog.show = false
      if(action){
        action()
      }
    },
    switchTab(index){
      this.activeTabIndex = index
      this.loadData()
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    async loadSquare(is_live){
      const script = document.createElement('script');
      if(is_live){
        script.src = 'https://web.squarecdn.com/v1/square.js'
      } else {
        script.src = 'https://sandbox.web.squarecdn.com/v1/square.js'
      }
      script.async = true;
      document.body.appendChild(script);
    },
    // Stripe stored payment methods
    // Square stored payment methods
    newGiftCard(){
      this.modals.new = true
    },
    async toDataURL(url) {
      try {
        var result = await axios({
          method: "get",
          url: url + '?' + (Math.random() + 1).toString(36).substring(7),
          responseType: 'blob',
          crossdomain: true
        });
        let blobUrl = URL.createObjectURL(result.data);
        return blobUrl

      }
      catch (err) {
        return "";
      }
    },
    async downloadPDF(item, event){
      if(event){
        event.preventDefault()
      }
      this.selectedGiftCard = item
      this.gift_card_img = await this.toDataURL(this.selectedGiftCard.design_url)
      this.logo = await this.toDataURL(this.group.img)
      await this.$refs.html2Pdf.generatePdf()

    },
    async beforeDownload ({ html2pdf, options, pdfContent }) {
      await html2pdf().set(options).from(pdfContent).toPdf().get('pdf').then((pdf) => {
        const totalPages = pdf.internal.getNumberOfPages()
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i)
          pdf.setFontSize(10)
          pdf.setTextColor(150)
          pdf.text('Page ' + i + ' of ' + totalPages, (pdf.internal.pageSize.getWidth() * 0.88), (pdf.internal.pageSize.getHeight() - 0.3))
        }
      }).save()
    },
    async sendGiftCardEmail(item){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/transactions/' + item.transaction_id + '/gift-cards'
      let data = {
        email: JSON.parse(item.recipients)[0].email,
        gift_card_id: item.id
      }

      this.axios.post(url, data, config)
        .then(response => {
          this.showAlert('Success', 'Gift card delivery email sent to customer', 'success')
          this.getGiftPurchases()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    sendGiftCardEmailPrompt(item, event){
      if(event){
        event.preventDefault()
      }
      this.showConfirm({
        title: 'Are you sure?',
        message: 'This will send the gift card delivery email to the recipient.',
        confirmText: 'Confirm',
        onConfirm: () => this.sendGiftCardEmail(item)
      })
    },
    deactivateGiftCardPrompt(item, action, event){
      if(event){
        event.preventDefault()
      }
      let text = "This action will deactivate the gift card. You will need to manually refund any remaining balance."
      if(action === 1){
        text = "This action will activate the gift card."
      }
      this.showConfirm({
        title: 'Are you sure?',
        message: text,
        confirmText: 'Confirm',
        confirmVariant: action === 1 ? 'primary' : 'danger',
        onConfirm: () => this.deactivateGiftCard(item, action)
      })
    },
    async deactivateGiftCard(item, action){
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gifts/' + item.id

      let data = {
        activate: action
      }

      this.loading = true
      axios.put(url, data, config)
        .then(response => {
          this.loading = false

          let text = "Gift card has been deactivated"
          if(action === 1){
            text = "Gift card has been activated"
          }

          this.showAlert('Success', text, 'success')

          this.getGiftPurchases()
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async getAllGiftCards(config, count){
      let url = ''
      let promises = []
      let csvData = []

      let offset = 0
      while(offset <= this.totalRows){

        url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gifts?count=' + count + "&offset=" + offset
        promises.push(
          axios.get(url, config)
            .then(response =>{
              csvData.push(response.data.gift_purchases)
            })
            .catch(err => {
                console.log(err)
                if (err.response.status == 401) {
                  this.$router.push('/forbidden')
                } else {
                  this.showAlert('Error', 'Something went wrong', 'danger')
                }
              }
            )
        )
        offset = offset + count
      }

      await Promise.all(promises)

      // merge all response arrays into one
      let arr = []
      csvData.forEach((element) => {
        arr = arr.concat(element)

      })
      return arr
    },
    escapeCSVValue(value) {
      if (typeof value === 'string') {
        // Escape double quotes by doubling them
        value = value.replace(/"/g, '""');

        // If the value contains a comma, wrap it in double quotes
        if (value.includes(',') || value.includes('\n')) {
          value = `"${value}"`;
        }
      }
      return value;
    },
    async downloadCSV(){
      //get the entire data set
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      this.loading = true
      const count = 5000
      const csvData = await this.getAllGiftCards(config, count)
      this.loading = false

      let csv = 'Code, Gift Card Type, Customer First Name, Customer Last Name, Customer Email,' +
        'Recipient First Name, Recipient Last Name, Recipient Email, Status, ' +
        'Purchased Value, Remaining Value, Price, Discount, Fee,' +
        'Transaction Total, Transaction Paid, Transaction Due,' +
        'Purchase Date, Activation Date, Delivery Date, Expiration Date' +
        (this.enable_utm_source_tracking ? ', UTM Source, UTM Medium, UTM Campaign, UTM Referrer' : '') + '\n';
      csvData.forEach(row => {
        csv += [this.escapeCSVValue(this.getGiftCardCode(row.code)),
          this.escapeCSVValue(this.getGiftCardTypeName(row.gift_card_type_id)),
          this.escapeCSVValue(row.first_name), this.escapeCSVValue(row.last_name),
          this.escapeCSVValue(row.email),
          row.recipients ? this.escapeCSVValue(JSON.parse(row.recipients)[0].first_name) : '',
          row.recipients ? this.escapeCSVValue(JSON.parse(row.recipients)[0].last_name) : '',
          row.recipients ? this.escapeCSVValue(JSON.parse(row.recipients)[0].email) : '',
          row.is_active ? 'Active' : 'Deactivated',
          this.getAmount(row.total), this.getAmount(row.remaining), this.getAmount(row.price),
          this.getAmount(row.discount || 0), this.getAmount(row.fee || 0),
          this.getAmount(row.transaction_total), this.getAmount(row.transaction_paid), this.getAmount(row.transaction_due),
          JSON.stringify(this.getLocalDate(row.date_purchased) + ' ' + this.getLocalTime(row.date_purchased)),
          JSON.stringify(this.getFormattedDate(row.activation_date)),
          JSON.stringify(this.getDeliveryDate(row.delivery_date, row.date_purchased)),
          row.expiration_date ? JSON.stringify(this.getFormattedDate(row.expiration_date)) : 'Does not expire']
          .concat(this.enable_utm_source_tracking ? [
            this.escapeCSVValue(this.capitalizeFirstLetter(row.utm_source) || ''),
            this.escapeCSVValue(row.utm_medium || ''),
            this.escapeCSVValue(row.utm_campaign || ''),
            this.escapeCSVValue(row.utm_referrer || '')
          ] : [])
        csv += "\n";
      });

      const anchor = document.createElement('a');
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      anchor.target = '_blank';
      anchor.download = 'gift_purchases.csv';
      anchor.click();
    },
    getRecipients(r){
      return JSON.parse(r)
    },
    getAmount(integer) {
      if (integer == null) {
        return;
      }

      // Get currency info
      const currencyInfo = _.find(this.currencies, { code: this.currency });
      const symbol = currencyInfo ? currencyInfo.symbol : this.currency_symbol;
      const isPostfix = currencyInfo ? currencyInfo.postfix : false;

      let formattedAmount;

      if (this.number_format === 1) {
        // European formatting (comma as decimal separator)
        formattedAmount = integer.toLocaleString(undefined, { minimumFractionDigits: 2 }).replace('.', ',');
      } else {
        // Standard formatting (dot as decimal separator)
        formattedAmount = parseFloat(integer).toFixed(2);
      }

      if (this.currency_format === 0) {
        // Symbol format
        if (isPostfix) {
          return formattedAmount + symbol;  // 12.34kr
        } else {
          return symbol + formattedAmount;  // $12.34
        }
      } else {
        // Currency code format
        return formattedAmount + ' ' + this.currency;  // 12.34 SEK
      }
    },
    getDayOfWeek(d){
      if(!d){
        return
      }

      if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
        if(moment.isMoment(d)) {
          return d.format('dddd')
        }
        return dayOfWeek(d) + ', '
      }
      return ''
    },
    getLocalDate(d){
      return formatGroupDate(d)
    },
    getLocalTime(d){
      return formatGroupTime(d)
    },
    getFormattedDate(d){
      if(moment.isMoment(d) || d instanceof Date) {
        d = moment(d).format('YYYY-MM-DD')
      }
      return this.getDayOfWeek(d) + formattedDate(this.date_format, d)
    },
    getDeliveryDate(delivery_date, date_purchased){
      if(!delivery_date){
        return this.getLocalDate(date_purchased)
      } else {
        // return delivery_date
        return this.getFormattedDate(delivery_date)
      }
    },
    getGiftCardCode(code){
      if(!code){
        return
      }
      if(isNaN(code)){
        return code
      }
      let c = code.match(/.{1,4}/g);
      return c.join(' ')
    },
    async copyGiftCardCode(code){
      try {
        await navigator.clipboard.writeText(code)
        this.$notify({
          message: 'Gift card code copied',
          type: 'success',
          icon: 'tim-icons icon-check-2',
          horizontalAlign: 'right',
          verticalAlign: 'top',
          timeout: 2000
        })
      } catch (err) {
        const textArea = document.createElement('textarea')
        textArea.value = code
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$notify({
          message: 'Gift card code copied',
          type: 'success',
          icon: 'tim-icons icon-check-2',
          horizontalAlign: 'right',
          verticalAlign: 'top',
          timeout: 2000
        })
      }
    },
    getGiftCardTypeName(typeId){
      if(!typeId || !this.gift_card_types || this.gift_card_types.length === 0){
        return null
      }
      const type = this.gift_card_types.find(t => t.id === typeId)
      return type ? type.name : null
    },
    // Expiration line for the gift card PDF, matching the delivery email: blank when the
    // type opts out, the formatted date when present, otherwise "Does not expire".
    getGiftCardExpirationDisplay(giftCard){
      if(!giftCard || !this.gift_card_types){
        return ''
      }
      const type = this.gift_card_types.find(t => t.id === giftCard.gift_card_type_id)
      if(!type || Number(type.show_expiration_date) !== 1){
        return ''
      }
      return giftCard.expiration_date
        ? 'Expires on ' + this.getFormattedDate(giftCard.expiration_date)
        : 'Does not expire'
    },
    viewCustomer(id){
      this.$router.push({ path: '/customers/details', query: { id:  id }})
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async loadStripe(){
      const stripeExists = document.getElementById('stripev3');
      if (!stripeExists) {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.id = 'stripev3';
        document.body.appendChild(script);
      }
    },
    getCategories(items){

      const groupedArray = [];

      items.forEach(item => {
        const { category_id, category, minimum_participants, default_participants } = item;

        // Find if the category_id already exists in groupedArray
        let group = groupedArray.find(g => g.category_id === category_id);

        if (!group) {
          // If it doesn't exist, create a new group
          group = {
            category_id: category_id,
            category: category,
            minimum_participants: minimum_participants,
            default_participants: default_participants,
            number_of_players: 1,
            pricing: []
          };
          groupedArray.push(group);
        }

        group.pricing.push(item);
      });
      return groupedArray;
    },
    async getGiftCardTypes(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gift-card-types'
      this.loading = true
      this.axios.get(url, config)
        .then(response => {
          this.gift_card_types = response.data.gift_card_types
          this.addQueryParameters()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })

    },
    async saveGiftCardSettings(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}

      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gift-card-settings'
      let data = {
        title: this.gift_card_settings.title,
        subtext: this.gift_card_settings.subtext,
        button_text: this.gift_card_settings.button_text,
        image_id: this.gift_card_settings.image_id,
      }
      this.axios.put(url, data, config)
        .then(response => {
          this.showAlert('Success', "Gift card settings have been updated", 'success')
          this.getGiftCardSettings()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async getGiftCardSettings(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}

      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gift-card-settings'
      this.axios.get(url, config)
        .then(response => {
          this.gift_card_settings = response.data.gift_card_settings
          this.booking_site_settings = response.data.booking_site_settings
          this.addQueryParameters()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async loadPaystack(){
      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v2/inline.js'
      script.async = true;
      document.body.appendChild(script);
    },
    async getGiftPurchases(source){
      if(source === 'filter'){
        this.currentPage = 1
      }
      let count = this.perPage
      let offset = Number((this.currentPage * this.perPage) - this.perPage)
      // FilterBar flat values: text fields + status + gift_card_type id.
      const f = this.filterValues

      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/gifts?count=' + count + '&offset=' + offset +
        '&customer=' + encodeURIComponent(f.customer) +
        '&recipient=' + encodeURIComponent(f.recipient) +
        '&status=' + encodeURIComponent(f.status) +
        '&code=' + encodeURIComponent(f.code) +
        '&gift_card_type=' + encodeURIComponent(f.gift_card_type)
      this.loading = true
      this.axios.get(url, config)
        .then(response => {
          this.gift_purchases = response.data.gift_purchases
          this.totalRows = response.data.total_rows
          this.fees = response.data.fees
          this.games = response.data.games
          this.gift_card_types = response.data.gift_card_types
          for(let n in this.gift_card_types){
            if(typeof this.gift_card_types[n].linked_game_ids === 'string'){
              this.gift_card_types[n].linked_game_ids = JSON.parse(this.gift_card_types[n].linked_game_ids)
            }
            this.gift_card_types[n].game_display_mode = this.gift_card_types[n].game_display_mode || 0
          }
          this.settings = response.data.settings
          this.enable_utm_source_tracking = response.data.enable_utm_source_tracking

          this.fields = [
            'select',
            { key: 'gift_card', label: 'Gift card' },
            { key: 'people', label: 'People' },
            { key: 'value', label: 'Value' },
            { key: 'dates', label: 'Dates' },
            'actions'
          ]

          for(let n in this.games){
            this.games[n].game_pricing = JSON.parse(this.games[n].game_pricing)
            this.games[n].pricing_categories = this.getCategories(this.games[n].game_pricing)
          }

          if(this.settings.payment_gateway_id === 2){
            this.loadSquare(this.settings.is_live)
          }
          if(this.settings.payment_gateway_id === 3){
            this.loadStripe()
          }

          if(this.settings.payment_gateway_id === 4){
            this.loadPaystack()
          }

          for(let n in this.gift_purchases){
            if(this.select_all_gift_cards){
              this.gift_purchases[n].selected = true
            } else {
              let found = _.find(this.selectedGiftCards, {id: this.gift_purchases[n].id})
              if(found){
                this.gift_purchases[n].selected = true
              }
            }

          }
          this.addQueryParameters()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    addQueryParameters() {
      this.$nextTick(() => {
        this.currentPage = this.$route.query.page
      });

      const newQuery = {
        ...this.$route.query,
        count: this.perPage,
        page: this.currentPage,
        customer: this.filterValues.customer,
        recipient: this.filterValues.recipient,
        status: this.filterValues.status,
        code: this.filterValues.code,
        gift_card_type: this.filterValues.gift_card_type || '',
        tab: this.activeTabIndex
      }
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
    loadData(){
      // Tab 0 (purchases): the shared <filter-bar> owns the initial fetch and drives
      // it via @change (onFiltersChanged), so don't fetch here or it double-loads.
      if(Number(this.activeTabIndex) === 1){
        this.getGiftCardTypes()
      } else if(Number(this.activeTabIndex) === 2){
        this.getGiftCardSettings()
      }
    }
  },
  mounted() {
    this.date_format_cal = this.date_format
    this.date_format_cal =  this.date_format_cal.replaceAll('Y', 'y')
    this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }

    this.country = _.find(this.countries, { code: JSON.parse(localStorage.getItem('group')).country })
    this.currency_symbol =  _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
    // Default when the query omits count/page, otherwise Number(undefined) => NaN.
    this.perPage = Number(this.$route.query.count) || 10
    this.currentPage = Number(this.$route.query.page) || 1
    this.loading = true

    // Seed FilterBar's flat values from the URL (gift_card_type = id). FilterBar
    // resolves saved-views precedence and drives the first fetch via its initial
    // `change` (onFiltersChanged) on tab 0; the page does not self-fetch there.
    const q = this.$route.query || {}
    this.filterValues = {
      code: q.code || '',
      customer: q.customer || '',
      recipient: q.recipient || '',
      status: q.status || '',
      gift_card_type: q.gift_card_type ? Number(q.gift_card_type) : ''
    }
    let tab = this.$route.query.hasOwnProperty('tab') ? this.$route.query.tab : 0
    this.switchTab(Number(tab))
  }
}
</script>

<style scoped>
/* Gift card cell styling */
.gc-info-cell {
  white-space: nowrap;
}

.gc-code-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
}

.gc-meta {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.gc-info-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.08);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  color: #fff;
}

.gc-info-badge i {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
}

.gc-info-badge--refund {
  background: rgba(253, 126, 20, 0.15);
  color: #fd7e14;
}

.gc-info-badge--refund i {
  color: #fd7e14;
}

.gc-code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.5px;
  font-weight: 500;
}

.gc-code--block {
  display: block;
  width: 100%;
  text-align: center;
}

.gc-copy-btn {
  opacity: 0.5;
  transition: opacity 0.2s;
  color: #c8c8c8 !important;
}

.gc-copy-btn:hover {
  opacity: 1;
  color: #c8c8c8 !important;
}

.gc-status {
  display: inline-flex;
  align-items: center;
  padding: 4px 0;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.gc-status--active {
  color: #42b883;
}

.gc-status--inactive {
  color: #f5365c;
}

/* People column styling */
.people-cell {
  white-space: nowrap;
}

.person-entry--recipient {
  margin-top: 6px;
}

.person-header {
  display: flex;
  align-items: center;
}

.person-label {
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 5px;
  border-radius: 2px;
  margin-right: 6px;
}

.person-label--from {
  background-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.8);
}

.person-label--to {
  background-color: rgba(225, 78, 202, 0.2);
  color: #e14eca;
}

.person-email {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.9);
  margin-left: 38px;
}

.person-email i {
  font-size: 9px;
  margin-right: 2px;
}

.person-badge-row {
  margin-left: 38px;
  margin-top: 2px;
}

.gc-value-chip--highlight {
  background-color: rgba(66, 186, 150, 0.2);
  border: 1px solid rgba(66, 186, 150, 0.4);
}

/* Expiration states */
.gc-date-chip--warning {
  background-color: rgba(255, 178, 43, 0.15);
  border: 1px solid rgba(255, 178, 43, 0.4);
}

.gc-date-chip--danger {
  background-color: rgba(253, 93, 147, 0.15);
  border: 1px solid rgba(253, 93, 147, 0.4);
}

/* "Never expires" state */
.gc-date-chip--never {
  background-color: rgba(66, 186, 150, 0.1);
  border: 1px solid rgba(66, 186, 150, 0.25);
}

</style>

<style>
/* Light mode overrides */
.white-content .gc-code {
  background-color: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.8);
}

.white-content .gc-status--active {
  color: #2dce89;
}

.white-content .gc-status--inactive {
  color: #f5365c;
}

.white-content .person-label--from {
  background-color: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.6);
}

.white-content .person-label--to {
  background-color: rgba(225, 78, 202, 0.15);
  color: #d63384;
}

.white-content .person-email {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .gc-info-badge {
  background: #f0f2f5;
  color: #344675;
}

.white-content .gc-info-badge i {
  color: #525f7f;
}

.white-content .gc-info-badge--refund {
  background: rgba(253, 126, 20, 0.1);
  color: #e06b10;
}

.white-content .gc-info-badge--refund i {
  color: #e06b10;
}

.white-content .gc-value-chip--highlight {
  background-color: rgba(66, 186, 150, 0.12);
  border-color: rgba(66, 186, 150, 0.3);
}

.white-content .gc-date-chip--warning {
  background-color: rgba(255, 178, 43, 0.1);
  border-color: rgba(255, 178, 43, 0.3);
}

.white-content .gc-date-chip--danger {
  background-color: rgba(220, 53, 69, 0.1);
  border-color: rgba(220, 53, 69, 0.3);
}

.white-content .gc-date-chip--never {
  background-color: rgba(40, 167, 69, 0.08);
  border-color: rgba(40, 167, 69, 0.2);
}

.gc-details-popover {
  background: #272740 !important;
  border: 1px solid rgba(225, 78, 202, 0.25) !important;
  border-radius: 6px !important;
  padding: 12px 14px !important;
  min-width: 180px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.6) !important;
}
.gc-details-popover .popper__arrow {
  border-right-color: #272740 !important;
}
.gc-details-popover .popper__arrow::after {
  border-right-color: #272740 !important;
}
.gc-details-popover .gc-pop-section {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255,255,255,0.4);
  margin-bottom: 6px;
}
.gc-details-popover .gc-pop-label {
  color: rgba(255,255,255,0.7);
  font-size: 12px;
}
.gc-details-popover .gc-pop-value {
  color: #fff;
  font-size: 12px;
}
.gc-details-popover .gc-pop-divider {
  border-top: 1px solid rgba(255,255,255,0.1);
  margin: 8px 0;
}
.white-content .gc-details-popover {
  background: #f5f6fa !important;
  border: 1px solid rgba(225, 78, 202, 0.2) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}
.white-content .gc-details-popover .gc-pop-section {
  color: rgba(0,0,0,0.4);
}
.white-content .gc-details-popover .gc-pop-label {
  color: rgba(0,0,0,0.55);
}
.white-content .gc-details-popover .gc-pop-value {
  color: #1d1d2b;
}
.white-content .gc-details-popover .gc-pop-divider {
  border-top-color: rgba(0,0,0,0.1);
}
.white-content .gc-details-popover .popper__arrow {
  border-right-color: #f5f6fa !important;
}
.white-content .gc-details-popover .popper__arrow::after {
  border-right-color: #f5f6fa !important;
}
</style>
